import { useNavigate } from 'react-router-dom';
import { Stack, SimpleGrid, Center } from '@/components/ui/layout';
import { Title, Text, Button, Badge } from '@/components/ui/core';
import { Card } from '@/components/ui/data-display';
import { Box } from '@mantine/core';
import { IconUser, IconSettings, IconUsers, IconLock, IconChartBar, IconFileText, IconServer, IconDeviceDesktop, IconSend, IconFolder, IconBrain, IconDeviceMobile, IconAddressBook } from '@tabler/icons-react';
import { FadeIn, SlideIn } from '@/components/animations';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';
import { useDomainAccess } from '@/hooks/useDomainAccess';
import MainLayout from '@/layouts/MainLayout';
import { useDomainPrimaryBadge } from '@/domain/badges/useDomainPrimaryBadge';
import { useUserBadgesStore } from '@/domain/badges/userBadgesStore';
import type { DomainConfig } from '@/config/domains';

export default function MainPage() {
  const { user } = useEnrichedAuth();
  const { accessibleDomains, hasAnyDomainAccess } = useDomainAccess();
  const navigate = useNavigate();

  // Check if user has admin role
  const isAdmin = user?.roles?.includes('Admin') || false;
  const isLLMAdmin = user?.roles?.includes('LLM Admin') || isAdmin;

  // Live count of locked-out users for the admin "Users" tile. Polled by
  // <UserBadgeSubscriber /> mounted inside <BadgeSubscribers /> in
  // MainLayout. Falls back to undefined (no badge) when zero.
  const lockedOutUsers = useUserBadgesStore((s) => s.lockedOut);

  // Public domains - always accessible to authenticated users
  const publicDomains = [
    {
      // The CCMS mobile apps are private — you cannot find them by searching the App
      // Store — so this page is the only way in. It needs to be discoverable from the
      // home screen, not just buried in the sidebar.
      key: 'get-the-apps',
      label: 'Get the Apps',
      description: 'Install the CCMS mobile apps and pick up your access code.',
      icon: <IconDeviceMobile size={48} />,
      basePath: '/app/apps',
    },
    {
      key: 'profile',
      label: 'Profile',
      description: 'Manage your personal information and preferences.',
      icon: <IconUser size={48} />,
      basePath: '/profile',
    },
    {
      key: 'settings',
      label: 'Settings',
      description: 'Configure your account settings and preferences.',
      icon: <IconSettings size={48} />,
      basePath: '/settings',
    },
  ];

  // LLM Admin domains - accessible to users with LLM Admin or Admin role
  const llmAdminDomains = isLLMAdmin ? [
    {
      key: 'ai-management',
      label: 'AI Management',
      description: 'AI workflows, content generation, document Q&A, and analytics.',
      icon: <IconBrain size={48} />,
      basePath: '/app/ai',
    },
    {
      key: 'ai-settings',
      label: 'AI Settings',
      description: 'Feature flags, model selection, presets, and usage logs.',
      icon: <IconBrain size={48} />,
      basePath: '/admin/ai',
    },
  ] : [];

  // Admin domains - only accessible to users with Admin role
  const adminDomains = isAdmin ? [
    {
      key: 'admin-users',
      label: 'Users',
      description: 'Manage system users and their accounts.',
      icon: <IconUsers size={48} />,
      basePath: '/admin/users',
      badge: lockedOutUsers > 0 ? lockedOutUsers : undefined,
      badgeColor: 'red',
    },
    {
      key: 'admin-members',
      label: 'Members',
      description: 'Member directory and profiles. Search, add, merge and update member records.',
      icon: <IconAddressBook size={48} />,
      basePath: '/admin/members',
    },
    {
      key: 'admin-permissions',
      label: 'Permissions',
      description: 'Configure roles and permissions.',
      icon: <IconLock size={48} />,
      basePath: '/admin/permissions',
    },
    {
      key: 'admin-reports',
      label: 'Reports',
      description: 'View system reports and analytics.',
      icon: <IconChartBar size={48} />,
      basePath: '/admin/reports',
    },
    {
      key: 'admin-system-log',
      label: 'System Log',
      description: 'Real-time system logs from application, API, and database.',
      icon: <IconFileText size={48} />,
      basePath: '/admin/system-log',
    },
    {
      key: 'admin-system',
      label: 'System',
      description: 'Monitor system health, services status, and configuration.',
      icon: <IconServer size={48} />,
      basePath: '/admin/system',
    },
    {
      key: 'admin-presence',
      label: 'Presence Management',
      description: 'System-wide presence analytics and user activity tracking.',
      icon: <IconDeviceDesktop size={48} />,
      basePath: '/admin/presence',
    },
    {
      key: 'admin-infrastructure',
      label: 'Infrastructure',
      description: 'Monitor and manage RabbitMQ queues and Redis cache.',
      icon: <IconServer size={48} />,
      basePath: '/admin/infrastructure',
    },
    {
      key: 'admin-communications',
      label: 'Communications',
      description: 'Manage email, SMS, WhatsApp, and push notifications.',
      icon: <IconSend size={48} />,
      basePath: '/admin/communications',
    },
    {
      key: 'admin-file-management',
      label: 'File Management',
      description: 'Browse, upload, and manage files stored in AWS S3.',
      icon: <IconFolder size={48} />,
      basePath: '/admin/file-management',
    },
  ] : [];

  // Only show no access message if user has no domains AND is not admin AND has no public domains
  // (Admin users always have access to admin sections, and all users have Profile/Settings)
  const hasAnyAccess = hasAnyDomainAccess || isAdmin || isLLMAdmin || publicDomains.length > 0;

  if (!hasAnyAccess) {
    return (
      <MainLayout>
        <Center style={{ minHeight: 'calc(100vh - 200px)' }}>
            <FadeIn delay={200}>
              <Card variant="elevated" p="md" radius="md">
                <Stack align="center" gap="md">
                  <Box
                    style={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      background: 'var(--mantine-color-red-1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Text size="xl" c="red">
                      🔒
                    </Text>
                  </Box>
                  <Title order={2}>No Access</Title>
                  <Text size="sm" c="dimmed" ta="center" maw={400}>
                    You don't have access to any domains yet. Please contact your administrator
                    to request access.
                  </Text>
                  <Button
                    variant="light"
                    onClick={() => navigate('/support')}
                    mt="md"
                  >
                    Contact Support
                  </Button>
                </Stack>
              </Card>
            </FadeIn>
          </Center>
      </MainLayout>
    );
  }

  // Render domain selection cards
  return (
    <MainLayout>
      <Stack gap="xl">
          {/* Welcome Message */}
          <FadeIn delay={200}>
            <Stack gap="xs" align="flex-start">
              <Title order={1}>
                Welcome back, {user?.displayName || user?.firstName || 'User'}!
              </Title>
              <Text c="dimmed" size="lg">
                Select an app to continue working
              </Text>
            </Stack>
          </FadeIn>

          {/* Existing Domains Section */}
          <Stack gap="md" align="flex-start">
            <Title order={2}>App</Title>
            {accessibleDomains.length > 0 ? (
              <SimpleGrid cols={{ base: 1, sm: 1, md: 2, lg: 3 }} spacing="lg" style={{ width: '100%' }}>
                {[...accessibleDomains].sort((a, b) => a.label.localeCompare(b.label)).map((domain, index) => (
                  <AccessibleDomainTile
                    key={domain.key}
                    domain={domain}
                    index={index}
                    onNavigate={() => navigate(domain.basePath)}
                  />
                ))}
              </SimpleGrid>
            ) : (
              <FadeIn delay={300}>
                <Card variant="elevated" p="lg" radius="md">
                  <Stack align="center" gap="sm">
                    <Text c="dimmed" size="sm" ta="center">
                      No apps assigned to you yet.
                    </Text>
                    <Text c="dimmed" size="xs" ta="center">
                      Contact your administrator to request access.
                    </Text>
                  </Stack>
                </Card>
              </FadeIn>
            )}
          </Stack>

          {/* Public Domains Section */}
          <Stack gap="md" align="flex-start">
            <Title order={2}>User</Title>
            <SimpleGrid cols={{ base: 1, sm: 1, md: 2, lg: 3 }} spacing="lg" style={{ width: '100%' }}>
              {publicDomains.map((domain, index) => (
                <SlideIn key={domain.key} direction="up" delay={300 + index * 100}>
                  <Card
                    variant="elevated"
                    p="sm"
                    radius="md"
                    style={{
                      height: '100%',
                      cursor: 'pointer',
                      position: 'relative',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = 'var(--mantine-shadow-lg)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '';
                    }}
                    onClick={() => navigate(domain.basePath)}
                  >
                    <Box style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', width: '100%', overflow: 'hidden' }}>
                      {/* Large Icon - Left */}
                      <Box
                        style={{
                          width: 80,
                          height: 80,
                          minWidth: 80,
                          flexShrink: 0,
                          borderRadius: 12,
                          background: 'var(--mantine-color-blue-1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'var(--mantine-color-blue-6)',
                        }}
                      >
                        {domain.icon}
                      </Box>

                      {/* Content - Right */}
                      <Stack gap="xs" style={{ flex: 1, minWidth: 0, overflow: 'hidden' }} align="flex-start">
                        {/* Title */}
                        <Title order={5} style={{ width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {domain.label}
                        </Title>

                        {/* Description */}
                        <Text size="xs" c="dimmed" lineClamp={2} style={{ textAlign: 'left', minHeight: '2.5em', width: '100%' }}>
                          {domain.description}
                        </Text>
                      </Stack>
                    </Box>
                  </Card>
                </SlideIn>
              ))}
            </SimpleGrid>
          </Stack>

          {/* AI Management Section - Only show if user has LLM Admin or Admin role */}
          {isLLMAdmin && llmAdminDomains.length > 0 && (
            <Stack gap="md" align="flex-start">
              <Title order={2}>AI</Title>
              <SimpleGrid cols={{ base: 1, sm: 1, md: 2, lg: 3 }} spacing="lg" style={{ width: '100%' }}>
                {llmAdminDomains.map((domain, index) => (
                  <SlideIn key={domain.key} direction="up" delay={300 + index * 100}>
                    <Card
                      variant="elevated"
                      p="sm"
                      radius="md"
                      style={{
                        height: '100%',
                        cursor: 'pointer',
                        position: 'relative',
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-4px)';
                        e.currentTarget.style.boxShadow = 'var(--mantine-shadow-lg)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '';
                      }}
                      onClick={() => navigate(domain.basePath)}
                    >
                      <Box style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', width: '100%', overflow: 'hidden' }}>
                        <Box
                          style={{
                            width: 80,
                            height: 80,
                            minWidth: 80,
                            flexShrink: 0,
                            borderRadius: 12,
                            background: 'var(--mantine-color-violet-1)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'var(--mantine-color-violet-6)',
                          }}
                        >
                          {domain.icon}
                        </Box>
                        <Stack gap="xs" style={{ flex: 1, minWidth: 0, overflow: 'hidden' }} align="flex-start">
                          <Title order={5} style={{ width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {domain.label}
                          </Title>
                          <Text size="xs" c="dimmed" lineClamp={2} style={{ textAlign: 'left', minHeight: '2.5em', width: '100%' }}>
                            {domain.description}
                          </Text>
                        </Stack>
                      </Box>
                    </Card>
                  </SlideIn>
                ))}
              </SimpleGrid>
            </Stack>
          )}

          {/* Admin Domains Section - Only show if user has admin role */}
          {isAdmin && adminDomains.length > 0 && (
            <Stack gap="md" align="flex-start">
              <Title order={2}>Admin</Title>
              <SimpleGrid cols={{ base: 1, sm: 1, md: 2, lg: 3 }} spacing="lg" style={{ width: '100%' }}>
                {adminDomains.map((domain, index) => (
                  <SlideIn key={domain.key} direction="up" delay={300 + index * 100}>
                    <Card
                      variant="elevated"
                      p="sm"
                      radius="md"
                      style={{
                        height: '100%',
                        cursor: 'pointer',
                        position: 'relative',
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-4px)';
                        e.currentTarget.style.boxShadow = 'var(--mantine-shadow-lg)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '';
                      }}
                      onClick={() => navigate(domain.basePath)}
                    >
                      {/* Badge - Top Right */}
                      {domain.badge && (
                        <Box
                          style={{
                            position: 'absolute',
                            top: '12px',
                            right: '12px',
                            zIndex: 1,
                          }}
                        >
                          <Badge
                            color={domain.badgeColor || (typeof domain.badge === 'number' ? 'red' : 'gray')}
                            variant="filled"
                            size="lg"
                            circle={typeof domain.badge === 'number'}
                          >
                            {domain.badge}
                          </Badge>
                        </Box>
                      )}

                      <Box style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', width: '100%', overflow: 'hidden' }}>
                        {/* Large Icon - Left */}
                        <Box
                          style={{
                            width: 80,
                            height: 80,
                            minWidth: 80,
                            flexShrink: 0,
                            borderRadius: 12,
                            background: 'var(--mantine-color-red-1)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'var(--mantine-color-red-6)',
                          }}
                        >
                          {domain.icon}
                        </Box>

                        {/* Content - Right */}
                        <Stack gap="xs" style={{ flex: 1, minWidth: 0, overflow: 'hidden' }} align="flex-start">
                          {/* Title */}
                          <Title order={5} style={{ width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {domain.label}
                          </Title>

                          {/* Description */}
                          <Text size="xs" c="dimmed" lineClamp={2} style={{ textAlign: 'left', minHeight: '2.5em', width: '100%' }}>
                            {domain.description}
                          </Text>
                        </Stack>
                      </Box>
                    </Card>
                  </SlideIn>
                ))}
              </SimpleGrid>
            </Stack>
          )}
        </Stack>
    </MainLayout>
  );
}

/**
 * AccessibleDomainTile — single tile in the accessible-domains grid.
 * Owns its own `useDomainPrimaryBadge` subscription so React's rules-of-
 * hooks aren't violated by the parent's `.map`.
 *
 * Live count takes precedence over the static `domain.badge` fallback
 * baked into [config/domains.tsx](src/config/domains.tsx); when the live
 * value is `null` (no count or user lacks feature access) the tile falls
 * back to whatever the static config carries.
 */
function AccessibleDomainTile({
  domain,
  index,
  onNavigate,
}: {
  domain: DomainConfig;
  index: number;
  onNavigate: () => void;
}) {
  const live = useDomainPrimaryBadge(domain.key);
  const badgeValue = live?.count ?? domain.badge ?? null;
  const badgeColor = live?.color ?? domain.badgeColor ?? (typeof domain.badge === 'number' ? 'red' : 'gray');

  return (
    <SlideIn direction="up" delay={300 + index * 100}>
      <Card
        variant="elevated"
        p="sm"
        radius="md"
        style={{
          height: '100%',
          cursor: 'pointer',
          position: 'relative',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-4px)';
          e.currentTarget.style.boxShadow = 'var(--mantine-shadow-lg)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '';
        }}
        onClick={onNavigate}
      >
        {badgeValue != null && (
          <Box
            style={{
              position: 'absolute',
              top: '12px',
              right: '12px',
              zIndex: 1,
            }}
          >
            <Badge
              color={badgeColor}
              variant="filled"
              size="lg"
              circle={typeof badgeValue === 'number'}
            >
              {badgeValue}
            </Badge>
          </Box>
        )}

        <Box style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', width: '100%', overflow: 'hidden' }}>
          <Box
            style={{
              width: 80,
              height: 80,
              minWidth: 80,
              flexShrink: 0,
              borderRadius: 12,
              background: 'var(--mantine-color-indigo-1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--mantine-color-indigo-6)',
            }}
          >
            {domain.icon}
          </Box>
          <Stack gap="xs" style={{ flex: 1, minWidth: 0, overflow: 'hidden' }} align="flex-start">
            <Title order={5} style={{ width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {domain.label}
            </Title>
            <Text size="xs" c="dimmed" lineClamp={2} style={{ textAlign: 'left', minHeight: '2.5em', width: '100%' }}>
              {domain.description}
            </Text>
          </Stack>
        </Box>
      </Card>
    </SlideIn>
  );
}
